/**
 * sovereign_cockpit — the Sovereign's REAL 3-D flight cockpit: a captain's chair,
 * a curved forward console with glowing MFD screens, and canopy struts framing an
 * open forward view of space. Unlike the fighter canopies (a 2-D screen overlay),
 * the Sovereign has a walkable interior, so its first-person view seats you INSIDE
 * a 3-D cabin that flies with the ship.
 *
 * Built rig-local (the ship noses −Z, so forward is −Z) and parented to the flight
 * rig exactly like the ship mesh (which renders), shown only when the Sovereign is
 * zoomed into cockpit view. Self-lit MeshBasicMaterial (the INTERIOR-MATERIALS law:
 * baked/basic materials draw regardless of the sector lighting).
 */
import * as THREE from 'three/webgpu';

/** Seated eye position (rig-local): in the chair, looking down the nose (−Z). */
export const SOVEREIGN_EYE = { y: 1.55, z: 0.4 };

const HULL = 0x1b2432;    // dark cabin panels
const HULL2 = 0x252f40;   // lighter trim
const TRIM = 0x6a86b8;    // brushed-metal edge
const SEAT = 0x2a2440;    // upholstery (regal purple-grey)
const SCREEN = 0x2fd6ff;  // MFD glow (cyan)
const SCREEN2 = 0xc2acff; // secondary glow (violet)
const AMBER = 0xffc061;   // cabin accent lights

function box(w: number, h: number, d: number, color: number, x: number, y: number, z: number, rx = 0): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), new THREE.MeshBasicMaterial({ color }));
  m.position.set(x, y, z);
  if (rx) m.rotation.x = rx;
  return m;
}

/** Build the Sovereign flight-cockpit group (rig-local). */
export function buildSovereignCockpit(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'sovereign-cockpit';

  // ── Cabin shell: floor, low side walls, a rear bulkhead behind the chair ──
  g.add(box(3.6, 0.12, 4.2, HULL, 0, 0.5, 0.2));                 // floor
  g.add(box(0.14, 1.5, 4.2, HULL, -1.8, 1.25, 0.2));            // left wall
  g.add(box(0.14, 1.5, 4.2, HULL, 1.8, 1.25, 0.2));             // right wall
  g.add(box(3.6, 1.6, 0.14, HULL, 0, 1.3, 2.3));               // rear bulkhead
  g.add(box(3.6, 0.12, 4.2, HULL2, 0, 2.5, 0.2));              // ceiling
  // ceiling accent strip
  const strip = box(0.12, 0.04, 3.6, AMBER, 0, 2.42, 0.2); (strip.material as THREE.MeshBasicMaterial).color.multiplyScalar(1); g.add(strip);

  // ── Captain's chair (behind the camera; you sit in it) ──
  g.add(box(1.0, 0.16, 1.0, SEAT, 0, 1.02, 1.15));             // seat pan
  g.add(box(1.0, 1.2, 0.16, SEAT, 0, 1.7, 1.65, -0.14));       // backrest (reclined)
  g.add(box(0.14, 0.5, 0.9, HULL2, -0.55, 1.25, 1.1));         // left armrest
  g.add(box(0.14, 0.5, 0.9, HULL2, 0.55, 1.25, 1.1));          // right armrest
  g.add(box(0.7, 0.3, 0.14, SEAT, 0, 2.35, 1.75, -0.14));      // headrest
  g.add(box(0.5, 0.8, 0.5, HULL, 0, 0.6, 1.35));               // pedestal base
  // armrest control pads (glow)
  g.add(box(0.24, 0.03, 0.4, SCREEN, -0.55, 1.5, 1.05));
  g.add(box(0.24, 0.03, 0.4, SCREEN2, 0.55, 1.5, 1.05));

  // ── Forward console: a low curved dashboard below the sightline ──
  for (let i = -2; i <= 2; i++) {
    const a = i * 0.28;
    const seg = box(0.72, 0.5, 0.4, HULL2, Math.sin(a) * 1.5, 0.95 + Math.abs(i) * 0.04, -1.9 + Math.abs(i) * 0.12);
    seg.rotation.y = -a;
    g.add(seg);
  }
  // dashboard lip trim
  g.add(box(3.0, 0.05, 0.1, TRIM, 0, 1.22, -1.75));
  // three MFD screens angled up toward the pilot
  const screenColors = [SCREEN, SCREEN2, SCREEN];
  for (let i = 0; i < 3; i++) {
    const sx = (i - 1) * 0.72;
    const scr = box(0.6, 0.34, 0.03, screenColors[i], sx, 1.18, -1.66, -0.6);
    (scr.material as THREE.MeshBasicMaterial).transparent = true;
    (scr.material as THREE.MeshBasicMaterial).opacity = 0.92;
    g.add(scr);
    // bezel
    g.add(box(0.66, 0.4, 0.02, HULL, sx, 1.17, -1.7, -0.6));
  }
  // console button rows (little glow chips)
  for (let r = 0; r < 2; r++) for (let c = 0; c < 8; c++) {
    g.add(box(0.05, 0.02, 0.05, c % 3 === 0 ? AMBER : TRIM, -0.8 + c * 0.23, 1.02 + r * 0.08, -1.5));
  }
  // twin throttle levers on the right console
  g.add(box(0.06, 0.3, 0.06, TRIM, 0.95, 1.15, -1.2, -0.3));
  g.add(box(0.06, 0.3, 0.06, TRIM, 1.1, 1.12, -1.2, -0.3));

  // ── Canopy struts: an A-frame that FRAMES the forward view but leaves it open ──
  g.add(box(0.14, 0.14, 2.6, HULL, -1.5, 2.2, -0.6, 0.5));      // left overhead rail
  g.add(box(0.14, 0.14, 2.6, HULL, 1.5, 2.2, -0.6, 0.5));       // right overhead rail
  g.add(box(0.16, 2.2, 0.16, HULL, -1.55, 1.4, -1.9, 0));       // left A-pillar
  g.add(box(0.16, 2.2, 0.16, HULL, 1.55, 1.4, -1.9, 0));        // right A-pillar
  g.add(box(3.3, 0.16, 0.16, HULL, 0, 2.45, -1.9));            // top rail
  // strut tint highlight
  g.add(box(3.3, 0.04, 0.04, TRIM, 0, 2.36, -1.9));

  return g;
}
